import { useEffect, useRef } from 'react';
import { TokensColorThemeList } from '../models/dist-tokens/Theme';
import colorsObj from '../styles/colors.json';

/**
 *
 * @param classes -
 * @returns A string of CSS classes
 */
export const makeClass: (
  classes: Array<string | boolean | undefined>
) => string = (classes: Array<string | boolean | undefined>) =>
  classes
    .filter((e) => e)
    .join(' ')
    .trim();

/**
 * Returns the browser window if available.
 */
export const WINDOW: Window | undefined =
  (typeof window === 'object' && window) || undefined;

/**
 * Checks if DOM Exist or not.
 */
export const DOMExists = !!(
  WINDOW &&
  WINDOW.document &&
  WINDOW.document.createElement
);

export type CSSVarType = {
  [key: string]: string | number | undefined;
};
export type CSSVarTypeReturn = {
  [key: string]: string | number;
};

/**
 * Returns valid CSS styles for inline use with `style` React prop.
 * @param obj - Inline CSS styles.
 * @returns Object with valid CSS style values.
 */
export const setCSSVar = (obj: CSSVarType): CSSVarTypeReturn => {
  Object.keys(obj).forEach((key) => obj[key] === undefined && delete obj[key]);

  return obj as CSSVarTypeReturn;
};

/**
 * Capitalise a string.
 * @param s - A string to capitalise
 * @returns string
 */
export const capitalise = (s: string): string =>
  s.replace(/./, (m) => m.toUpperCase());

type Function = (...invokingArguments: any) => void;
export const bindRaf = (fn: Function): Function => {
  let isRunning = false;
  let args: any = null;

  const run = () => {
    isRunning = false;
    fn(...args);
  };

  return function (...invokingArguments: any) {
    args = invokingArguments;

    if (isRunning) {
      return;
    }

    isRunning = true;
    requestAnimationFrame(run);
  };
};
/*
 * Flattens a multi-dimensional array into one level.
 *
 * {array} lists - The array to flatten.
 *
 * @returns {array} - A flat array.
 */
export const flatten = (lists: Array<any>): Array<any> =>
  lists.reduce((a: any, b: any) => a.concat(b), []);

/**
 * Inverses the key:value pair inside object to value:key
 * @param obj
 */
export const inverseObject = (obj: object): object =>
  Object.assign({}, ...Object.entries(obj).map(([a, b]) => ({ [b]: a })));

/**
 * Tests if string doesn't exists or is empty
 * @param str
 */
export const isStrEmpty = (str: string): boolean =>
  str === undefined ||
  str === null ||
  typeof str !== 'string' ||
  !((str?.trim?.() || '').length > 0);
/**
 * Map Keyboard keys for readability
 */

export const KEYBOARD = {
  ESCAPE: 'Escape',
  ENTER: 'Enter',
  ARROWUP: 'ArrowUp',
  ARROWDOWN: 'ArrowDown',
  ARROWRIGHT: 'ArrowRight',
  ARROWLEFT: 'ArrowLeft',
  TAB: 'Tab'
};

export type ThemeType = {
  [mode in 'light' | 'dark' | 'bold']: {
    [type in 'primary' | 'secondary' | 'sparkle']: string[];
  };
};
/**
 * Creates a theme from the given set of colors
 * @param colorNames  List of colors in the order of `primary`, `secondary` and `sparkle`
 * @param name name of the Theme. Ex: Retro
 * @returns {Object} The class name of the theme, Ex: theme--Retro and an object of colors
 */
export const createTheme = (
  colorNames: string[],
  name = 'JIO-Custom'
): {
  themeClass: string;
  colorsList: ThemeType | {};
} => {
  if (!WINDOW || colorNames?.length < 3)
    return { themeClass: '', colorsList: [] };

  const paletteColors = colorsObj;

  const themeClass = `theme--${name}`;
  const colors = {
    primary: colorNames[0],
    secondary: colorNames[1],
    sparkle: colorNames[2]
  };
  const colorsList: ThemeType = {
    dark: {
      primary: [],
      secondary: [],
      sparkle: []
    },
    light: {
      primary: [],
      secondary: [],
      sparkle: []
    },
    bold: {
      primary: [],
      secondary: [],
      sparkle: []
    }
  };
  Object.keys(colors).forEach((color) => {
    const modes = Object.keys(colorsList);
    modes.forEach((mode) => {
      const key = `${colors[color]}-${mode}`;
      TokensColorThemeList.filter((item) => item.includes(color)).forEach(
        (colorThemeType) => {
          const colorSplit = colorThemeType.split('-');
          colorSplit.shift();
          const shade = colorSplit.join('-');
          const colorValue = paletteColors[`${key}-${shade}`];
          const colorKey = `--color-${colorThemeType}`;
          colorsList[mode][color].push(`${colorKey}: ${colorValue};`);
        }
      );
    });
  });

  const textContent = `${Object.keys(colorsList)
    .map((mode) => {
      return Object.keys(colorsList[mode])
        .map((colorThemeType) => {
          return `${mode === 'light' ? `.${themeClass},` : ''} 
          .${themeClass} [data-mode=${mode}], .${themeClass} .data-mode-${mode} {
      ${colorsList[mode][colorThemeType].join('')}
      }
      `;
        })
        .join('');
    })
    .join('')}`;

  const blob = new Blob([textContent], { type: 'text/css' });

  const existingTag = document.getElementById(themeClass);
  const finalLinkTag = (existingTag ||
    document?.createElement('link')) as HTMLLinkElement;

  finalLinkTag.setAttribute('id', `${themeClass}`);
  finalLinkTag.href = URL.createObjectURL(blob);
  finalLinkTag.rel = 'stylesheet';
  if (!existingTag) {
    document.querySelector('head')?.appendChild(finalLinkTag);
  }

  return { themeClass, colorsList };
};

/**
 * Delays the invocation of the function until wait milliseconds have passed since the last call of the debounced function.
 * @param fn - Function that needs to debounce
 * @param wait - time for debouncing
 * @param immediate - Can be passed to run the function at the start and then debounce subsequent calls
 * @returns Debounced Function
 */
export const debounce = (
  fn: Function,
  wait: number,
  immediate = false
): Function => {
  let timeoutId: NodeJS.Timeout | null;
  return function (this: any, ...args: any[]) {
    const later = () => {
      timeoutId = null;
      if (!immediate) fn.apply(this, args);
    };
    const callNow = immediate && !timeoutId;
    timeoutId !== null && clearTimeout(timeoutId);
    timeoutId = setTimeout(later, wait);
    if (callNow) fn.apply(this, args);
  };
};

export function spaceBeforeCapital(str: string): string {
  if (str.length < 1) return '';
  return (
    str.charAt(0).toUpperCase() +
    str
      .slice(1)
      .replace(/([A-Z])/g, ' $1')
      .trim()
  );
}
/**
 * Combine several refs into one ref. Useful to create internal refs for components and use them with a forward ref
 * @param refs - List of refs to be combined
 * @returns A combined ref
 */
export const useCombinedRefs = <ElementType>(
  ...refs: (
    | React.ForwardedRef<ElementType>
    | ((current: ElementType) => void)
  )[]
) => {
  const targetRef = useRef<ElementType>(null);

  useEffect(() => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === 'function' && targetRef.current) {
        ref(targetRef.current);
      } else {
        (ref as React.MutableRefObject<ElementType | null>).current =
          targetRef.current;
      }
    });
  }, [refs]);

  return targetRef;
};

export const equalArrays = <P>(source: P[] = [], target: P[] = []) => {
  if (source.length !== target.length) return false;
  return target.every((item) => source.includes(item));
};

export const flexGapNotSupported = () => {
  const flex = document.createElement('div');
  flex.style.display = 'flex';
  flex.style.flexDirection = 'column';
  flex.style.rowGap = '1px';

  // create two, elements inside it
  flex.appendChild(document.createElement('div'));
  flex.appendChild(document.createElement('div'));

  // append to the DOM (needed to obtain scrollHeight)
  document.body.appendChild(flex);
  const isNotSupported = flex.scrollHeight !== 1; // flex container should be 1px high from the row-gap
  flex.parentNode.removeChild(flex);

  return isNotSupported;
};
